import React from 'react';
import { Check, Loader2, AlertTriangle, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useCountUp, Sparkline } from '../../../shared/Motion';

// BREAK-MGMT premium presentation primitives shared by the four tabs. Visual language only — no data, rules or behaviour.
// Typography follows the TitleFlow standard (h1 22 / h2 16 / body 13–13.5 / th 11 / controls 12.5–13).

export const BreakBanner = ({ icon: Icon, title, subtitle, quote, testId }) => (
  <section className="bm-rise relative overflow-hidden rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-indigo-50/70 to-slate-50 shadow-[0_8px_24px_-14px_rgba(37,99,235,.35)] px-5 py-4 flex items-center gap-4 mb-5" data-testid={testId}>
    <svg className="absolute right-0 top-0 h-full w-[46%] opacity-70 pointer-events-none" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 120 C60 70 120 110 180 60 S300 40 400 90 L400 120 Z" fill="#dbeafe" />
      <path d="M100 120 C160 90 220 100 280 55 S360 60 400 80 L400 120 Z" fill="#c7d2fe" opacity=".6" />
    </svg>
    <div className="h-12 w-12 shrink-0 rounded-xl bg-white/90 text-blue-600 flex items-center justify-center shadow-sm relative"><Icon className="h-6 w-6" /></div>
    <div className="relative flex-1 min-w-[220px]">
      <h1 className="text-[22px] font-bold text-slate-900 tracking-tight leading-tight">{title}</h1>
      <p className="text-[12.5px] text-slate-600 mt-0.5">{subtitle}</p>
    </div>
    {quote && <p className="relative hidden lg:block max-w-[240px] text-right text-[12.5px] italic text-slate-600 leading-snug pr-1" data-testid="bm-banner-quote">{quote}</p>}
  </section>
);

export const BreakPageHeader = ({ title, subtitle, action, testId }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap mb-4" data-testid={testId}>
    <div>
      <h2 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
      {subtitle && <p className="text-[12.5px] text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const TONES = {
  green: { tile: 'bg-emerald-50 text-emerald-600', line: '#10b981' },
  red: { tile: 'bg-rose-50 text-rose-500', line: '#ef4444' },
  blue: { tile: 'bg-sky-50 text-sky-600', line: '#3b82f6' },
  purple: { tile: 'bg-violet-50 text-violet-600', line: '#7c3aed' },
  amber: { tile: 'bg-amber-50 text-amber-600', line: '#d97706' },
};

/** Premium KPI card. `value` numeric → count-up; `display` overrides the rendered value (e.g. "1h 26m").
 *  `trend` = { pct: number|null, label } — pct null → neutral "—" pill (never fabricated). `series` → real sparkline. */
export const BreakKpiCard = ({ icon: Icon, tone = 'blue', label, value, display, sub, trend, series, onClick, loading, testid, delay = 0, emphasis = false }) => {
  const t = TONES[tone] || TONES.blue;
  const n = useCountUp(typeof value === 'number' ? value : null);
  const Tag = onClick ? 'button' : 'div';
  const pct = trend?.pct; const up = pct > 0; const flat = pct === 0;
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} disabled={onClick ? loading : undefined} data-testid={testid} style={{ animationDelay: `${delay}ms` }}
      className={`bm-rise text-left w-full bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-12px_rgba(15,23,42,.16)] p-4 flex items-center gap-3.5 min-h-[92px] ${onClick ? 'transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_2px_4px_rgba(15,23,42,.06),0_16px_32px_-12px_rgba(15,23,42,.2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:cursor-progress' : ''}`}>
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${t.tile}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1 min-w-0">
        <div className={`text-[24px] font-bold leading-tight tabular-nums ${emphasis ? 'text-rose-600' : 'text-slate-900'}`} data-testid={`${testid}-value`}>
          {loading ? <span className="inline-block h-6 w-10 rounded bg-slate-100 animate-pulse align-middle" /> : (display ?? (n ?? 0).toLocaleString('en-US'))}
        </div>
        <div className="text-[12.5px] text-slate-500 truncate">{label}</div>
        {trend && !loading && (
          <div className="mt-1 flex items-center gap-1.5 text-[11px]" data-testid={`${testid}-trend`}>
            <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px font-semibold ${pct == null || flat ? 'bg-slate-100 text-slate-500' : (up ? (trend.upIsBad ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700') : (trend.upIsBad ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'))}`}>
              {pct == null || flat ? <Minus className="h-3 w-3" /> : up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{pct == null ? '—' : `${Math.abs(pct)}%`}
            </span>
            <span className="text-slate-400">{trend.label}</span>
          </div>
        )}
        {sub}
      </div>
      {series && series.length > 1 && <div className="self-center shrink-0 hidden md:block"><Sparkline data={series} color={t.line} w={88} h={36} testid="bm-sparkline" /></div>}
    </Tag>
  );
};

export const BreakFilterBar = ({ children, actions, testId }) => (
  <div className="bm-rise border border-slate-200/80 rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] px-4 py-3 flex flex-wrap items-end gap-x-3 gap-y-3 mb-4" data-testid={testId}>
    {children}
    {actions && <div className="ml-auto flex items-end gap-2">{actions}</div>}
  </div>
);

export const FilterField = ({ label, children, className = '' }) => (
  <div className={className}>
    <div className="text-[10.5px] text-slate-500 font-medium mb-1 whitespace-nowrap">{label}</div>
    {children}
  </div>
);

export const BreakTableCard = ({ children, testId, className = '' }) => (
  <div className={`bm-rise border border-slate-200/80 rounded-2xl overflow-hidden bg-white shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)] ${className}`} data-testid={testId}>{children}</div>
);

/** Premium table header cell content — label + optional sort indicator (functional only when `onSort` given). */
export const BreakTh = ({ label, sortKey, sort, onSort, align = 'left', className = '' }) => {
  const active = sortKey && sort?.key === sortKey;
  const Icon = !sortKey ? null : active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const inner = <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>{label}{Icon && <Icon className={`h-3 w-3 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />}</span>;
  return sortKey && onSort
    ? <button type="button" onClick={() => onSort(sortKey)} className={`bm-th-btn ${className}`} data-testid={`sort-${sortKey}`}>{inner}</button>
    : <span className={className}>{inner}</span>;
};

export const BreakStatusPill = ({ children, tone = 'slate', dot = false, testId, className = '' }) => {
  const cls = {
    slate: 'bg-slate-100 text-slate-600', green: 'bg-emerald-50 text-emerald-700', red: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-700', blue: 'bg-sky-50 text-sky-700', orange: 'bg-orange-50 text-orange-700', violet: 'bg-violet-50 text-violet-700',
  }[tone] || 'bg-slate-100 text-slate-600';
  const dotCls = { green: 'bg-emerald-500', red: 'bg-rose-500', amber: 'bg-amber-500', blue: 'bg-sky-500', orange: 'bg-orange-500', violet: 'bg-violet-500', slate: 'bg-slate-400' }[tone];
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors duration-200 ${cls} ${className}`} data-testid={testId}>{dot && <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />}{children}</span>;
};

export const BreakAvatar = ({ name, tone = 'indigo' }) => {
  const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const cls = { indigo: 'bg-indigo-100 text-indigo-700', emerald: 'bg-emerald-100 text-emerald-700', sky: 'bg-sky-100 text-sky-700' }[tone] || 'bg-indigo-100 text-indigo-700';
  return <span className={`h-8 w-8 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center ${cls}`} data-testid="bm-avatar">{initials}</span>;
};

export const BreakEmptyState = ({ title, subtitle, meta, tone = 'ok', icon, testId, action }) => (
  <div className={`bm-rise flex items-center gap-4 rounded-2xl border px-5 py-5 ${tone === 'ok' ? 'border-emerald-100 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-testid={testId}>
    <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${tone === 'ok' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
      {icon || <Check className="h-5 w-5" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13.5px] font-semibold text-slate-900">{title}</div>
      {subtitle && <div className="text-[12.5px] text-slate-500">{subtitle}</div>}
      {meta && <div className="text-[11px] text-slate-400 mt-0.5">{meta}</div>}
    </div>
    {action}
  </div>
);

export const BreakLoadingState = ({ label = 'Loading…', testId }) => (
  <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-slate-400" data-testid={testId}><Loader2 className="h-4 w-4 animate-spin" /> {label}</div>
);

export const BreakErrorState = ({ title = "Couldn't load this data.", detail, onRetry, testId }) => (
  <div className="bm-rise flex items-center gap-4 rounded-2xl border border-rose-200 bg-rose-50/60 px-5 py-4" role="alert" data-testid={testId}>
    <div className="h-10 w-10 shrink-0 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center"><AlertTriangle className="h-5 w-5" /></div>
    <div className="min-w-0 flex-1">
      <div className="text-[13.5px] font-semibold text-rose-800">{title}</div>
      {detail && <div className="text-[12px] text-rose-700/80 truncate">{detail}</div>}
    </div>
    {onRetry && <button type="button" onClick={onRetry} className="bm-btn h-8 px-3 rounded-lg border border-rose-200 bg-white text-[12.5px] font-medium text-rose-700 hover:bg-rose-100" data-testid="bm-retry-btn">Try again</button>}
  </div>
);

/** Compact pagination — "Showing X of Y <noun>" + ‹ 1 › */
export const BreakPagination = ({ page, pages, shown, total, noun = 'records', meta, onPrev, onNext, testId }) => (
  <div className="flex items-center justify-between flex-wrap gap-2 mt-3 text-[12px] text-slate-500" data-testid={testId}>
    <span data-testid="bm-pagination-summary">Showing {shown} of {total} {noun}{meta && <span className="text-slate-400"> · {meta}</span>}</span>
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={onPrev} disabled={page <= 1} aria-label="Previous page" className="bm-btn h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center justify-center" data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></button>
      <span className="h-8 min-w-8 px-2 rounded-lg bg-indigo-600 text-white text-[12px] font-semibold flex items-center justify-center shadow-sm" data-testid="pagination-page">{page}</span>
      <button type="button" onClick={onNext} disabled={page >= pages} aria-label="Next page" className="bm-btn h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center justify-center" data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></button>
    </div>
  </div>
);
